import { useCallback, useEffect, useState } from 'react';

import {
  CollaborativeDocumentFragment,
  CollaborativeDocumentQuery,
  CollaborativeDocumentQueryVariables,
  useCollaborativeDocumentQuery,
  useCollaborativeDocumentUpdatedSubscription,
  withStandardGetHook,
} from '@spotify/confidence-graphql';

import { PENDING_AI_GENERATION } from '../../utils/collaborativeDocument';
import { useGenerateCollaborativeDocument } from '../useGenerateCollaborativeDocument';

const useCollaborativeDocumentImpl = withStandardGetHook<
  CollaborativeDocumentQuery,
  CollaborativeDocumentFragment,
  'collaborativeDocument',
  CollaborativeDocumentQueryVariables
>(useCollaborativeDocumentQuery, 'collaborativeDocument', {
  fetchPolicy: 'cache-and-network',
});

type CollaborativeDocumentOptions = Parameters<
  typeof useCollaborativeDocumentImpl
>[0];

type CollaborativeDocumentResult = ReturnType<
  typeof useCollaborativeDocumentImpl
> & {
  isGenerating: boolean;
  generateReport: () => Promise<void>;
};

export const useCollaborativeDocument = (
  options: CollaborativeDocumentOptions,
): CollaborativeDocumentResult => {
  const [isGeneratingLocally, setIsGeneratingLocally] = useState(false);
  const { name } = options;

  // Subscribe to collaborative document updates to get real-time AI generation progress
  useCollaborativeDocumentUpdatedSubscription({});

  const result = useCollaborativeDocumentImpl(options);

  const hasPendingOp =
    result.collaborativeDocument?.pendingOperation?.type ===
    PENDING_AI_GENERATION;

  const isGenerating = isGeneratingLocally || hasPendingOp;

  // Sync local generating state with server's pending operation.
  useEffect(() => {
    if (hasPendingOp) {
      setIsGeneratingLocally(true);
    } else if (isGeneratingLocally) {
      setIsGeneratingLocally(false);
    }
  }, [hasPendingOp]); // eslint-disable-line react-hooks/exhaustive-deps

  const { generateCollaborativeDocument } = useGenerateCollaborativeDocument({
    onError: 'post-alert',
  });

  const generateReport = useCallback(async () => {
    if (!name || isGenerating) return;
    setIsGeneratingLocally(true);
    try {
      await generateCollaborativeDocument(name);
    } catch {
      setIsGeneratingLocally(false);
    }
  }, [name, isGenerating, generateCollaborativeDocument]);

  return {
    ...result,
    isGenerating,
    generateReport,
  };
};
